// CustomStats.tsx
import React from 'react';
import { useSearchResults, useSearchFilters, useSearchQuery } from '../contexts/SearchContext';
import { useIntl, FormattedMessage } from 'react-intl';
import { generateStatsText, showFinlandSubtitle as computeShowFinlandSubtitle } from '../utils/stats/generateStatsText';
import type { StatsLocale } from '../utils/stats/generateStatsText';

/**
 * Presentational display for stats: same structure always; only the strings change.
 * When finlandSubtitle is provided (static SRP), no FormattedMessage; otherwise uses react-intl.
 */
export interface CustomStatsDisplayProps {
  statsText: string;
  showFinlandSubtitle: boolean;
  /** When provided, render this instead of FormattedMessage (for static SSR, no IntlProvider). */
  finlandSubtitle?: string;
}

export const CustomStatsDisplay: React.FC<CustomStatsDisplayProps> = ({
  statsText,
  showFinlandSubtitle,
  finlandSubtitle,
}) => (
  <div>
    <h1 className="font-bold text-sm @lg:text-base">
      {statsText}
    </h1>
    {showFinlandSubtitle && (
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {finlandSubtitle != null ? (
          finlandSubtitle
        ) : (
          <FormattedMessage
            id="stats.finlandJobsSubtitle"
            defaultMessage="Jobs available in Finnish or remotely to Finnish residents"
            description="Subtitle explaining that jobs are available in Finnish or remotely to Finnish residents, shown below the job count when displaying all jobs in Finland"
          />
        )}
      </p>
    )}
  </div>
);

export interface CustomStatsProps {
  /** When provided, display this string instead of computing from SearchContext (static/SSR). */
  statsText?: string;
  /** When provided with statsText, whether to show the Finland jobs subtitle (static/SSR). */
  showFinlandSubtitle?: boolean;
  /** When provided with statsText (static SRP), Finland subtitle string so no FormattedMessage is used. */
  finlandSubtitle?: string;
}

/**
 * When statsText (and optionally showFinlandSubtitle) are provided, renders the display only (no hooks).
 * When finlandSubtitle is also provided, no FormattedMessage for the subtitle (static SSR).
 */
const CustomStats: React.FC<CustomStatsProps> = (props) => {
  const { statsText: staticStatsText, showFinlandSubtitle: staticShowFinlandSubtitle, finlandSubtitle } = props;

  if (staticStatsText !== undefined) {
    return (
      <CustomStatsDisplay
        statsText={staticStatsText}
        showFinlandSubtitle={Boolean(staticShowFinlandSubtitle)}
        finlandSubtitle={finlandSubtitle}
      />
    );
  }

  return <CustomStatsWithContext />;
};

function CustomStatsWithContext(): React.ReactElement {
  const { results } = useSearchResults();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const intl = useIntl();

  const nbHits = Number(results?.nbHits ?? 0);
  const locale = intl.locale as StatsLocale;
  const filtersObj = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};
  const searchQuery = typeof query === 'string' ? query : '';

  const statsText = generateStatsText({ nbHits, filters: filtersObj, query: searchQuery, locale });
  const showFinlandSubtitle = computeShowFinlandSubtitle(statsText, filtersObj, searchQuery);

  return <CustomStatsDisplay statsText={statsText} showFinlandSubtitle={showFinlandSubtitle} />;
}

export default CustomStats;
